import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import ImpContainer from '../../Atoms/ImpContainer/ImpContainer';
import ImpFlex from '../../Atoms/ImpFlex/ImpFlex';
import ImpPad from '../../Atoms/ImpPad/ImpPad';
import ImpSpacer from '../../Atoms/ImpSpacer/ImpSpacer';
import { colors } from '../../DesignSystem/colors';
import { elevation } from '../../DesignSystem/elevation';
import { TYPOGRAPHY } from '../../DesignSystem/typography';
import { zIndex } from '../../DesignSystem/zIndex';
import { Icons } from '../../Icons/Icons';
import ImpDivider from '../ImpDivider/ImpDivider';
import ImpIcon from '../ImpIcon/ImpIcon';
import { useDocumentOverflow } from '../ImpModal/ImpModal';
import ImpText from '../ImpText/ImpText';
const TRANSITION_DURATION_MS = 300;
const FLYOUT_MAX_WIDTH_PX = 600;
const FLYOUT_COLOR = colors.surface.surfaceLow;
const getSlideInAnimation = (from) => {
    return keyframes `
    0% {
      transform: translateX(${from === 'right' ? '100%' : '-100%'});
    }
    100% {
      transform: translateX(0);
    }
  `;
};
const getSlideOutAnimation = (from) => {
    return keyframes `
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(${from === 'right' ? '100%' : '-100%'});
    }
  `;
};
const fadeOut = keyframes `
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;
const FlyoutBackground = styled(ImpContainer) `
  z-index: ${zIndex.flyoutBackground};
  position: fixed;
  inset: 0px;
  overflow: hidden;
  background-color: rgba(244, 243, 239, 0.8);

  ${({ isClosing }) => !isClosing
    ? css ``
    : css `
          animation: ${fadeOut} ${TRANSITION_DURATION_MS}ms ease-out forwards;
        `}
`;
const getBorderSide = (from) => {
    return from === 'right' ? 'left' : 'right';
};
const FlyoutContent = styled.div `
  display: flex;
  flex-direction: row;
  width: auto;
  flex: 1 1 0%;
  overflow: hidden auto;
  position: fixed;
  top: 0px;
  bottom: 0px;
  z-index: 150000;
  max-width: 100%;
  box-shadow: ${elevation.primary3};
  outline: none !important;
  ${({ from }) => from}: 0;

  background-color: ${FLYOUT_COLOR};
  cursor: auto;

  border-${({ from }) => getBorderSide(from)}: 1px solid ${colors.border.borderVariant};
  @media (max-width: ${FLYOUT_MAX_WIDTH_PX}px) {
    border-${({ from }) => getBorderSide(from)}: none;
  }

  width: ${({ width }) => width};

  ${({ isClosing, from }) => !isClosing
    ? css `
          animation: ${getSlideInAnimation(from)} ${TRANSITION_DURATION_MS}ms
            cubic-bezier(0.23, 1.03, 0, 0.98) forwards;
        `
    : css `
          animation: ${getSlideOutAnimation(from)} ${TRANSITION_DURATION_MS}ms
            cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
        `};
`;
const Padding = styled(ImpPad) `
  width: 100%;
  height: fit-content;
`;
export default function Flyout({ from = 'right', width = `min(100%, ${FLYOUT_MAX_WIDTH_PX}px);`, children, isOpen, isClosing, contentContainerProps, title, padDefault = true, hideNav, hideBack, navTrailingIcon, onBackClick, close, }) {
    const [offset, setOffset] = useState(0);
    const FlyoutContentRef = useRef(null);
    const handleBackgroundClick = () => {
        close();
    };
    useEffect(() => {
        if (!isOpen) {
            setOffset(0);
        }
        const handleScroll = (event) => {
            const target = event.target;
            setOffset(target.scrollTop);
        };
        const scrollableElement = FlyoutContentRef.current;
        if (scrollableElement) {
            scrollableElement.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (scrollableElement) {
                scrollableElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isOpen]);
    useDocumentOverflow(isOpen || isClosing);
    const renderChildren = () => {
        if (padDefault) {
            return (React.createElement(Padding, { padding: "24px", mobilePadding: "8px 16px", fluid: true },
                React.createElement(ImpFlex, { direction: "column", fluid: true }, children)));
        }
        return children;
    };
    const isScrolled = offset > 0;
    return isOpen || isClosing ? (React.createElement(FlyoutBackground, { isOpen: isOpen, isClosing: isClosing, dataTestId: "flyout-container", onClick: handleBackgroundClick },
        React.createElement(FlyoutContent, { width: width, from: from, ref: FlyoutContentRef, isOpen: isOpen, isClosing: isClosing, dataTestId: "flyout-content", onClick: (e) => e.stopPropagation(), backgroundColor: colors.surface.surfaceLow, ...contentContainerProps },
            React.createElement(ImpFlex, { direction: "column", fluid: true },
                !hideNav && (React.createElement(ImpFlyoutNav, { onClose: close, onBack: onBackClick || close, title: title, isScrolled: isScrolled, hideBack: hideBack, trailingIcon: navTrailingIcon })),
                renderChildren())))) : (React.createElement(React.Fragment, null));
}
const FlyoutNavContainer = styled(ImpContainer) `
  background-color: ${FLYOUT_COLOR};
  position: sticky;
  top: 0;
  z-index: 1;
  transition: box-shadow 0.1s ease-in-out;

  ${({ isScrolled }) => isScrolled &&
    css `
      box-shadow: ${elevation.primary1};
    `}
`;
export function ImpFlyoutNav({ onBack, onClose, title, isScrolled, hideBack, trailingIcon, }) {
    return (React.createElement(FlyoutNavContainer, { width: "100%", isScrolled: isScrolled },
        React.createElement(ImpPad, { padding: "16px 24px", mobilePadding: "16px" },
            React.createElement(ImpFlex, { direction: "row", justify: "space-between", fluid: true },
                !hideBack ? (React.createElement(ImpIcon, { iconSrc: Icons.chevronLeft, onClick: onBack })) : (React.createElement(ImpSpacer, null)),
                trailingIcon ? (React.createElement(ImpIcon, { iconSrc: trailingIcon.iconSrc, onClick: trailingIcon.onClick })) : (React.createElement(ImpIcon, { iconSrc: Icons.close, onClick: onClose })))),
        title && React.createElement(ImpFlyoutTitle, { title: title, isScrolled: isScrolled })));
}
const TitleContainer = styled(ImpContainer) `
  display: block;
`;
const TitleText = styled(ImpText) `
  display: block;
  transition: font-size 0.1s ease-in-out, line-height 0.1s ease-in-out;

  ${({ isScrolled }) => isScrolled &&
    css `
      ${TYPOGRAPHY.headline2.desktop}
    `}
`;
function ImpFlyoutTitle({ title, isScrolled, }) {
    return (React.createElement(TitleContainer, { width: "100%" },
        React.createElement(ImpPad, { padding: "0px 32px 12px", mobilePadding: "0 16px 12px" },
            React.createElement(TitleText, { typography: "headline1", overflowStyle: "ellipsis", isScrolled: isScrolled }, title)),
        React.createElement(ImpDivider, null)));
}
//# sourceMappingURL=ImpFlyout.js.map